import React, {useCallback, useMemo} from "react";
import {ArrowBack} from "@mui/icons-material";
import classnames from "classnames";
import IconButton from "../../../../../../../components/base/button/icon_button";
import Button from "../../../../../../../components/base/button/button";
import ToastService from "../../../../../../../../core/services/toast";
import {PanelFooter} from "../../../../../../../components/layout";
import {FormControlLabel, Typography} from "@mui/material";
import {Switch} from "../../../../../../../components/base/switch";
import {ImportingSteps} from "../../types";
import {setReduxDialogState} from "../../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../../core/models/static/routes/redux-store-routes";
import {useAppDispatch} from "../../../../../../../redux";

/**
 * ImportingBottomActionBar Component
 *
 * Provides navigation controls for the import process steps.
 * Shows/hides based on current step and state conditions.
 *
 * @param {Object} props - Component props
 * @param {Object} props.state - Current import state
 * @param {Function} props.setStep - Function to update current step
 * @param {boolean} props.updatingStep - Whether step transition is in progress
 * @param {boolean} [props.showInteractiveCorrectionToggle] - Show the interactive correction switch
 * @param {boolean} [props.interactiveCorrectionEnabled] - Current toggle value
 * @param {Function} [props.setInteractiveCorrectionEnabled] - Toggle setter
 */
const ImportingBottomActionBar = ({
                                      state,
                                      setStep,
                                      onFinalize,
                                      updatingStep,
                                      showInteractiveCorrectionToggle = false,
                                      interactiveCorrectionEnabled = false,
                                      setInteractiveCorrectionEnabled,
                                  }) => {
    const dispatch = useAppDispatch();

    /**
     * Determines if the action bar should be visible
     */
    const show = useMemo(() => {
        if (state.step === ImportingSteps.importing) {
            // During importing: only show the footer if the toggle is available
            return showInteractiveCorrectionToggle;
        }
        return true;
    }, [state.step, showInteractiveCorrectionToggle]);

    /**
     * Nav controls (back button + next/finalize button) are hidden during import.
     * The footer is reduced to the switch-only mode.
     */
    const showNavControls = useMemo(() => state.step !== ImportingSteps.importing, [state.step]);

    /**
     * Determines if previous step button should be shown
     */
    const showPreviousStep = useMemo(() => ![ImportingSteps.selectDataSource].includes(state.step), [state.step]);

    /**
     * Determines the title for the previous step button
     */
    const previousStepTitle = useMemo(() => {
        if (updatingStep)
            return "Processing...";
        switch (state.step) {
            case ImportingSteps.mapColumns:
                return "Back to select data source";
            case ImportingSteps.review:
                return "Back to mapping columns";
            default:
                return '';
        }
    }, [state.step, updatingStep]);

    /**
     * Determines the title for the next step button
     */
    const nextStepTitle = useMemo(() => {
        if (updatingStep) {
            return "Proceeding...";
        }
        if (state.step === ImportingSteps.review) {
            return "Finalize";
        }
        return "Next Step";
    }, [state.step, updatingStep]);

    /**
     * Navigates the user to the previous step of the request.
     */
    const setPrevStep = useCallback(() => {
        let newStep;
        switch (state.step) {
            case ImportingSteps.mapColumns:
                newStep = ImportingSteps.selectDataSource;
                break;
            case ImportingSteps.review:
                newStep = ImportingSteps.mapColumns;
                break;
            default:
                break;
        }
        if (newStep) {
            setStep(newStep);
        }
    }, [state.step, setStep]);

    /**
     * Navigates the user to the next step of the request.
     * Validates current step state before proceeding.
     */
    const setNextStep = useCallback(async () => {
        let newStep;
        switch (state.step) {
            case ImportingSteps.selectDataSource:
                if (!state.selectedDataType) {
                    ToastService.warning({
                        title: "No dataset selected",
                        message: 'Please select a data type to continue',
                        hideTicketSubmission: true
                    });
                    return;
                } else if (!state.apiRequests) {
                    ToastService.warning({
                        title: "No file uploaded",
                        message: 'Please upload the file to continue',
                        hideTicketSubmission: true
                    });
                    return;
                }
                newStep = ImportingSteps.mapColumns;
                break;

            case ImportingSteps.mapColumns:
                break;
            case ImportingSteps.review:
                if (!state.apiRequests) {
                    ToastService.warning({
                        message: 'No data to import',
                        title: "Import Failed",
                        hideTicketSubmission: true
                    });
                    return;
                }
                if (state.preventativeIssues?.length > 0) {
                    const confirmed = await new Promise((resolve) => {
                        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                            open: true,
                            title: 'Unresolved Issues',
                            description: `There are a total of ${state.preventativeIssues.length} unresolved issue(s) that may cause errors during the import. Do you want to proceed anyway?`,
                            confirmButtonText: "Continue",
                            cancelButtonText: "Cancel",
                            maxWidth: 'sm',
                            onClose: (confirmed) => {
                                resolve(confirmed);
                                return Promise.resolve(true);
                            }
                        }));
                    })
                    if (!confirmed) {
                        return;
                    }
                }
                const success = await onFinalize?.();
                if (success) {
                    newStep = ImportingSteps.importing;
                }
                break;

            default:
                break;
        }

        if (newStep) {
            setStep(newStep);
        }
    }, [state.step, state.selectedDataType, state.apiRequests, state.preventativeIssues?.length, onFinalize, dispatch, setStep]);

    return (
        <>
            {
                show &&
                <PanelFooter>
                    <div className={'bottom-action-bar'}>
                        <div className={classnames('bottom-action-bar-content', {'with-previous-step': showPreviousStep && showNavControls})}>
                            <div className={'d-flex flex-column align-items-start py-2'}>
                                {/* Back button — hidden during import */}
                                {showNavControls && showPreviousStep && (
                                    <div className={'previous-step'}>
                                        <IconButton
                                            disabled={updatingStep}
                                            onPressed={setPrevStep}
                                            color={'paper.secondary'}
                                        >
                                            <ArrowBack className='icon small'/>
                                        </IconButton>
                                        <p>{previousStepTitle}</p>
                                    </div>
                                )}
                                {/* Interactive correction toggle — review + importing steps */}
                                {
                                    showInteractiveCorrectionToggle &&
                                    (state.step === ImportingSteps.review || state.step === ImportingSteps.importing) &&
                                    <FormControlLabel
                                        sx={{mt: showNavControls ? 1 : 0, ml: 0, mr: 1}}
                                        control={
                                            <Switch
                                                checked={interactiveCorrectionEnabled}
                                                onChange={(e) => setInteractiveCorrectionEnabled?.(e)}
                                                color={'secondary'}
                                                size={5}
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant={'body2'}
                                                color={'text.secondary'}
                                                sx={{
                                                    marginLeft: 1,
                                                    fontSize: (theme) => `${theme.typography.pxToRem(14)} !important`,
                                                    fontWeight: `${400} !important`,
                                                }}
                                            >
                                                Enable interactive error correction
                                            </Typography>
                                        }
                                    />
                                }
                            </div>
                            {/* Next / Finalize button — hidden during import */}
                            {
                                showNavControls &&
                                <Button
                                    disabled={updatingStep}
                                    onClick={setNextStep}
                                    color={'primary'}
                                    variant={'contained'}
                                    {...(state.step === ImportingSteps.mapColumns && {
                                        onClick: undefined,
                                        type: 'submit',
                                        form: 'import-csv-file-form',
                                    })}
                                >
                                    {nextStepTitle}
                                </Button>
                            }
                        </div>
                    </div>
                </PanelFooter>

            }
        </>
    );
}

export default ImportingBottomActionBar;
